export type FooterMode =
  | "text"
  | "logo"
  | "logo-text"
  | "icon"
  | "icon-text"
  | "avatar"
  | "avatar-text"
  | "custom";

/** @deprecated Use `mode`. Mantido para compatibilidade com embeds antigos. */
export type FooterVariant = "logo" | "text" | FooterMode;

export type FooterPosition = "inline" | "fixed";
export type FooterTheme = "light" | "dark" | "auto";
export type FooterPlatform = "shopify" | "nuvemshop" | "generic";
export type FooterLayout = "inline" | "stack";
/** Posicao do labelBefore em relacao ao logo */
export type LabelBeforePlacement = "left" | "top";
/** Posicao do labelAfter em relacao ao logo */
export type LabelAfterPlacement = "right" | "bottom";
/** Onde inserir o host em relacao ao target */
export type FooterInsert = "append" | "after" | "before";

export interface FooterAssets {
  /** Wordmark unico (ignora tema claro/escuro) */
  logo?: string;
  logoLight?: string;
  logoDark?: string;
  icon?: string;
  avatar?: string;
}

export interface FooterDisplayFlags {
  showLogo?: boolean;
  showText?: boolean;
  showAvatar?: boolean;
  showIcon?: boolean;
}

export interface FooterConfig {
  mode: FooterMode;
  /** @deprecated Use `mode` */
  variant?: FooterVariant;
  position: FooterPosition;
  theme: FooterTheme;
  /** @deprecated Atalho: inline = esquerda/direita, stack = acima/abaixo. Prefira labelBeforePlacement / labelAfterPlacement */
  layout?: FooterLayout;
  /** Texto opcional antes do logo/icone/avatar */
  labelBefore?: string;
  /** left = ao lado esquerdo; top = acima */
  labelBeforePlacement?: LabelBeforePlacement;
  /** Texto opcional depois do logo/icone/avatar */
  labelAfter?: string;
  /** right = ao lado direito; bottom = abaixo */
  labelAfterPlacement?: LabelAfterPlacement;
  /** Separador visual antes do badge (ex.: "|" ao lado do copyright) */
  separator?: string;
  /** append = dentro do target; after/before = irmao do target */
  insert?: FooterInsert;
  /** Cor de fundo do host (hex/rgb). Sobrescreve deteccao automatica. */
  backgroundColor?: string;
  /** Espacamento acima do host (ex.: "10px"). Util quando o badge fica logo abaixo do Nuvemshop. */
  marginTop?: string;
  link: string;
  target: string;
  platform: FooterPlatform;
  clientId?: string;
  /** Base publica do bundle (dist/v1) */
  cdnBase: string;
  /** Base publica dos logos padrao (assets/brand) */
  assetsBase?: string;
  /** Base publica dos JSONs de cliente (configs/clients) */
  clientsBase?: string;
  enabled: boolean;
  assets?: FooterAssets;
  display?: FooterDisplayFlags;
}

export type FooterContentPart =
  | { type: "text"; text: string; placement: "before" | "after" }
  | { type: "logo" | "icon" | "avatar"; src: string; alt: string };

export const DEFAULT_LINK = "https://pandorium.com.br";
export const DEFAULT_LABEL_AFTER = "Feito com Pandorium";
export const S3_BUCKET = "pandorium-web-572630831854-sa-east-1-an";
export const S3_REGION = "sa-east-1";
export const S3_ROOT = "pandorium-footers";
export const S3_PUBLIC_ROOT = `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/${S3_ROOT}`;
export const DEFAULT_CDN_BASE = `${S3_PUBLIC_ROOT}/dist/v1`;
export const DEFAULT_ASSETS_BASE = `${S3_PUBLIC_ROOT}/assets/brand`;
export const DEFAULT_CLIENTS_BASE = `${S3_PUBLIC_ROOT}/configs/clients`;
export const ROOT_ATTR = "data-pandorium-footer-root";

export const FOOTER_MODES: FooterMode[] = [
  "text",
  "logo",
  "logo-text",
  "icon",
  "icon-text",
  "avatar",
  "avatar-text",
  "custom",
];

export const MODE_DISPLAY: Record<
  Exclude<FooterMode, "custom">,
  Required<FooterDisplayFlags>
> = {
  text: { showLogo: false, showText: true, showAvatar: false, showIcon: false },
  logo: { showLogo: true, showText: false, showAvatar: false, showIcon: false },
  "logo-text": {
    showLogo: true,
    showText: true,
    showAvatar: false,
    showIcon: false,
  },
  icon: { showLogo: false, showText: false, showAvatar: false, showIcon: true },
  "icon-text": {
    showLogo: false,
    showText: true,
    showAvatar: false,
    showIcon: true,
  },
  avatar: {
    showLogo: false,
    showText: false,
    showAvatar: true,
    showIcon: false,
  },
  "avatar-text": {
    showLogo: false,
    showText: true,
    showAvatar: true,
    showIcon: false,
  },
};

export const PLATFORM_PRESETS: Record<
  FooterPlatform,
  Partial<FooterConfig>
> = {
  shopify: {
    target: "footer, .shopify-section-footer, #shopify-section-footer",
    position: "inline",
  },
  nuvemshop: {
    target: "footer, .footer, #footer",
    position: "inline",
  },
  generic: {
    target: "footer",
    position: "inline",
  },
};

export const DEFAULT_CONFIG: FooterConfig = {
  mode: "logo",
  position: "inline",
  theme: "auto",
  labelAfter: DEFAULT_LABEL_AFTER,
  link: DEFAULT_LINK,
  target: "footer",
  platform: "generic",
  cdnBase: DEFAULT_CDN_BASE,
  assetsBase: DEFAULT_ASSETS_BASE,
  clientsBase: DEFAULT_CLIENTS_BASE,
  enabled: true,
};
